import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { User, Mail, Lock, Eye, EyeOff, AlertTriangle, ArrowRight, MapPin, Bell, ShieldCheck } from "lucide-react";
import { useGoogleLogin } from "@react-oauth/google";
import useSeo from "../seo/useSeo";

export default function Signup() {
  useSeo("/signup");
  const { register, loginWithGoogle, t, formatApiError } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(name, email, password);
      nav("/onboarding", { replace: true });
    } catch (er) {
      setErr(formatApiError(er));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    flow: "implicit",
    onSuccess: async (tokenResponse) => {
      setErr("");
      setLoading(true);
      try {
        await loginWithGoogle(tokenResponse.access_token);
        nav("/home", { replace: true });
      } catch (er) {
        setErr(formatApiError(er));
      } finally {
        setLoading(false);
      }
    },
    onError: () => setErr("Google sign-in failed"),
  });

  return (
    <div data-testid="signup-screen" className="min-h-screen bg-mesh-gradient flex">

      {/* Left branding panel — desktop only */}
      <div className="hidden lg:flex lg:w-[44%] flex-col justify-between p-14 relative overflow-hidden">
        <div className="particle-orb top-20 left-10 w-72 h-72 bg-brand-500/20" />
        <div className="particle-orb bottom-20 right-8 w-60 h-60 bg-accent-cyan/15" style={{ animationDelay: "2.5s" }} />

        <Link to="/" className="flex items-center gap-2.5 group relative z-10">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-brand-500 to-accent-cyan flex items-center justify-center shadow-brand">
            <img src="/logo192.png" alt="CarTrack" className="w-5 h-5 object-contain" />
          </div>
          <span className="text-[15px] font-heading font-extrabold text-white group-hover:text-brand-300 transition-colors">CarTrack</span>
        </Link>

        <div className="relative z-10">
          <h2 className="font-heading text-[40px] font-black text-white tracking-[-0.02em] leading-[1.1] mb-4">
            Start tracking<br />
            <span className="gradient-text">in 5 minutes.</span>
          </h2>
          <p className="text-slate-400 text-[15px] leading-relaxed mb-10 max-w-xs">
            Create your free account and pair your Android head unit in minutes.
          </p>
          <div className="space-y-5">
            {[
              { icon: MapPin,      title: "No extra hardware",       desc: "Just install the APK on your head unit" },
              { icon: Bell,        title: "Telegram notifications",  desc: "Instant alerts for every car event" },
              { icon: ShieldCheck, title: "Free forever",            desc: "Zero subscription fees, open-source core" },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3.5">
                <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <Icon size={16} className="text-brand-300" />
                </div>
                <div>
                  <div className="text-[13px] font-bold text-white leading-none">{title}</div>
                  <div className="text-[12px] text-slate-400 mt-0.5">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[12px] text-slate-600 relative z-10">© {new Date().getFullYear()} CarTrack</p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-4 py-10 relative">
        <div className="particle-orb top-1/4 right-1/4 w-64 h-64 bg-brand-600/10 lg:hidden" />
        <div className="particle-orb bottom-1/4 left-1/4 w-48 h-48 bg-accent-cyan/10 lg:hidden" style={{ animationDelay: "3s" }} />

        <div className="w-full max-w-[400px] relative z-10">

          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-brand-500 to-accent-cyan flex items-center justify-center shadow-brand">
                <img src="/logo192.png" alt="CarTrack" className="w-6 h-6 object-contain" />
              </div>
              <span className="text-[16px] font-heading font-extrabold text-white">CarTrack</span>
            </Link>
          </div>

          <div className="bg-white/96 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-glow-md p-8">

            <div className="mb-7">
              <h1 className="text-[22px] font-heading font-extrabold text-ink">{t.createAccount || "Create your account"}</h1>
              <p className="text-[13px] text-ink-soft mt-1 font-body">{t.tagline || "Start tracking your vehicle in minutes"}</p>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-widest text-ink-muted font-body block mb-1.5">{t.name}</span>
                <div className="flex items-center border border-surface-border rounded-xl px-3.5 py-3 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/10 transition-all bg-surface-soft">
                  <User size={15} className="text-ink-muted mr-2.5 shrink-0" />
                  <input
                    data-testid="signup-name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    autoComplete="name"
                    className="flex-1 text-[14px] bg-transparent outline-none text-ink placeholder:text-ink-subtle font-body"
                  />
                </div>
              </label>

              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-widest text-ink-muted font-body block mb-1.5">{t.email}</span>
                <div className="flex items-center border border-surface-border rounded-xl px-3.5 py-3 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/10 transition-all bg-surface-soft">
                  <Mail size={15} className="text-ink-muted mr-2.5 shrink-0" />
                  <input
                    data-testid="signup-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="flex-1 text-[14px] bg-transparent outline-none text-ink placeholder:text-ink-subtle font-body"
                  />
                </div>
              </label>

              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-widest text-ink-muted font-body block mb-1.5">{t.password}</span>
                <div className="flex items-center border border-surface-border rounded-xl px-3.5 py-3 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/10 transition-all bg-surface-soft">
                  <Lock size={15} className="text-ink-muted mr-2.5 shrink-0" />
                  <input
                    data-testid="signup-password"
                    type={showPw ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    minLength={6}
                    autoComplete="new-password"
                    className="flex-1 text-[14px] bg-transparent outline-none text-ink placeholder:text-ink-subtle font-body"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="text-ink-muted hover:text-ink-soft transition-colors cursor-pointer ml-1"
                    aria-label="Toggle password visibility"
                  >
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </label>

              {err && (
                <div data-testid="signup-error" className="flex items-center gap-2.5 text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-xl px-3.5 py-2.5 font-semibold font-body">
                  <AlertTriangle size={14} className="shrink-0 text-red-500" />
                  {err}
                </div>
              )}

              <button
                data-testid="signup-submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 text-white text-[14px] font-bold hover:opacity-95 active:scale-[0.99] transition-all shadow-brand disabled:opacity-60 font-body cursor-pointer shimmer-btn flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
                    {t.loading}
                  </>
                ) : (
                  <>
                    {t.createAccount || "Create Account"}
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-surface-border" />
              <span className="text-[11px] text-ink-muted font-medium font-body">{t.or || "or continue with"}</span>
              <div className="flex-1 h-px bg-surface-border" />
            </div>

            <button
              data-testid="google-signup"
              onClick={() => googleLogin()}
              disabled={loading}
              className="w-full py-3 rounded-xl border border-surface-border bg-white text-ink text-[13px] font-semibold flex items-center justify-center gap-2.5 hover:bg-surface-soft hover:border-ink-subtle transition-all shadow-card-sm disabled:opacity-60 font-body cursor-pointer active:scale-[0.99]"
            >
              <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {t.continueWithGoogle || "Continue with Google"}
            </button>

            <p className="text-center text-[12px] text-ink-muted mt-6 font-body">
              {t.alreadyHave}{" "}
              <Link to="/login" className="text-brand-600 font-semibold hover:underline" data-testid="link-to-login">
                {t.login}
              </Link>
            </p>

          </div>
        </div>
      </div>
    </div>
  );
}
